import { useQuery } from '@apollo/client';
import { Select } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  PartnerOnboardingStatuses,
  eligibleCoordinatorsQuery,
  extractEligibleCoordinators,
  extractRegionalCoordContacts,
  regionalCoordContactsQuery,
} from './constants';

const MAX_PAGES = 25;

/**
 * Walk a connection to its last page, concatenating edges into the cached result. Kept
 * local because both callers are in this file and the shape is identical.
 */
function usePageToEnd(pageInfo, fetchMore, connectionKey) {
  const pagesFetched = useRef(0);

  useEffect(() => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor || !fetchMore) return;
    if (pagesFetched.current >= MAX_PAGES) return;
    pagesFetched.current += 1;
    fetchMore({
      variables: { after: pageInfo.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        const more = fetchMoreResult?.[connectionKey];
        if (!more) return prev;
        return {
          ...prev,
          [connectionKey]: {
            ...more,
            edges: [
              ...(prev?.[connectionKey]?.edges || []),
              ...(more.edges || []),
            ],
          },
        };
      },
    }).catch(() => {
      // A failed page leaves the options short rather than breaking the control; the
      // effect will not retry, so it cannot loop on a persistent error.
    });
  }, [pageInfo, fetchMore, connectionKey]);
}

/**
 * Eligible coordinators = contacts with a non-cancelled coordinator onboarding (labelled
 * with its status) OR contacts flagged as a regional coordinator (e.g. legacy coordinators
 * with no onboarding record). Each contact appears once; an onboarding status takes
 * precedence over the generic flag label.
 *
 * Shared by the create modal (where picking one is required for a Field Worker) and the
 * onboarding list filter, so both offer exactly the same set — a coordinator you can
 * assign must also be a coordinator you can filter by.
 */
export function useEligibleCoordinators({ skip = false } = {}) {
  const {
    data: coordData,
    loading: coordLoading,
    fetchMore: fetchMoreCoords,
  } = useQuery(eligibleCoordinatorsQuery, {
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: rcData,
    loading: rcLoading,
    fetchMore: fetchMoreRcs,
  } = useQuery(regionalCoordContactsQuery, {
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Both sources are fixed-page queries, so without this a coordinator past the first
  // page silently disappears from the create control and the list filter alike. Page to
  // exhaustion; MAX_PAGES only stops a pathological dataset spinning forever.
  usePageToEnd(
    coordData?.allPartnerOnboardings?.pageInfo,
    fetchMoreCoords,
    'allPartnerOnboardings',
  );
  usePageToEnd(
    rcData?.allContactsForAdmin?.pageInfo,
    fetchMoreRcs,
    'allContactsForAdmin',
  );

  const coordinators = useMemo(() => {
    const byContact = new Map();
    extractEligibleCoordinators(coordData).forEach((c) => {
      if (c.status === 'CANCELLED') return;
      if (!byContact.has(c.contactId)) {
        byContact.set(c.contactId, {
          contactId: c.contactId,
          fullName: c.contact?.fullName,
          statusLabel: PartnerOnboardingStatuses[c.status]?.label || c.status,
        });
      }
    });
    extractRegionalCoordContacts(rcData).forEach((c) => {
      if (!byContact.has(c._id)) {
        byContact.set(c._id, {
          contactId: c._id,
          fullName: c.fullName,
          statusLabel: 'Regional Coordinator',
        });
      }
    });
    return [...byContact.values()];
  }, [coordData, rcData]);

  return { coordinators, loading: coordLoading || rcLoading };
}

function SelectCoordinator({
  value,
  onChange,
  skip,
  placeholder,
  style,
  allowClear,
}) {
  const { coordinators, loading } = useEligibleCoordinators({ skip });
  // Deep-linked filters arrive with a value before the option lists resolve. antd renders
  // the raw value when no option matches, which would put a raw contact id on screen, so
  // stand in a label until the real option arrives (or say so if it never does).
  const isKnown = coordinators.some((c) => c.contactId === value);

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Select coordinator'}
      showSearch
      optionFilterProp="children"
      allowClear={allowClear}
      style={style}
      loading={loading}
    >
      {value && !isKnown && (
        <Select.Option key={value} value={value}>
          {loading ? 'Loading…' : 'Unknown coordinator'}
        </Select.Option>
      )}
      {coordinators.map((c) => (
        <Select.Option key={c.contactId} value={c.contactId}>
          {c.fullName || c.contactId}
          {' — '}
          {c.statusLabel}
        </Select.Option>
      ))}
    </Select>
  );
}

export default SelectCoordinator;
